import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { ShieldCheck, Star, Check, AlertCircle, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SCALE = ['Insuffisant', 'À améliorer', 'Satisfaisant', 'Très satisfaisant', 'Excellent'];

function DimensionRow({ dimKey, dim, value, onChange }) {
    return (
        <div className="bg-white rounded-xl border border-slate-200 p-4 relative">
            <label className="block text-sm font-medium text-slate-900 mb-3">{dim.label}</label>
            <div className="grid grid-cols-5 gap-1.5" role="radiogroup" aria-label={dim.label}>
                {[1, 2, 3, 4, 5].map(v => (
                    <button
                        type="button"
                        key={`${dimKey}-${v}`}
                        onClick={(e) => { e.preventDefault(); e.stopPropagation(); onChange(dimKey, v); }}
                        role="radio"
                        aria-checked={value === v}
                        aria-label={`${SCALE[v - 1]} (${v}/5)`}
                        data-testid={`dim-${dimKey}-${v}`}
                        className={`text-xs px-2 py-2 rounded-lg border transition relative z-10 ${
                            value === v
                                ? 'bg-[#681793] text-white border-[#681793]'
                                : 'bg-white text-slate-600 border-slate-200 hover:border-[#681793]/40'
                        }`}>
                        {v}
                    </button>
                ))}
            </div>
            {value && (
                <p className="text-xs text-slate-500 mt-2">{SCALE[value - 1]}</p>
            )}
        </div>
    );
}

export default function PublicSellerReviewForm() {
    const { token } = useParams();
    const [ctx, setCtx] = useState(null);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(true);
    const [dimensions, setDimensions] = useState({});
    const [freeComment, setFreeComment] = useState('');
    const [improvement, setImprovement] = useState('');
    const [wouldRecommend, setWouldRecommend] = useState(null);
    const [identityMode, setIdentityMode] = useState('first_name_initial');
    const [customName, setCustomName] = useState('');
    const [projectType, setProjectType] = useState('appartement');
    const [transactionType, setTransactionType] = useState('vente_principale');
    const [consent, setConsent] = useState(false);
    const [step, setStep] = useState(1); // 1 dimensions, 2 questions, 3 identity, 4 preview, 5 success
    const [submitting, setSubmitting] = useState(false);
    const [submittedResult, setSubmittedResult] = useState(null);

    useEffect(() => {
        axios.get(`${API}/public/seller-reviews/by-token/${token}`)
            .then(r => {
                setCtx(r.data);
                // marque started
                axios.post(`${API}/public/seller-reviews/by-token/${token}/start`).catch(() => {});
            })
            .catch(err => {
                setError(err?.response?.status === 410 ? 'Ce lien a expiré ou a déjà été utilisé.' : 'Lien invalide.');
            })
            .finally(() => setLoading(false));
    }, [token]);

    if (loading) {
        return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-[#681793]" /></div>;
    }
    if (error) {
        return (
            <div className="min-h-screen flex items-center justify-center p-4 bg-slate-50">
                <div className="bg-white rounded-2xl border border-slate-200 p-8 max-w-md text-center">
                    <AlertCircle className="w-12 h-12 text-rose-500 mx-auto mb-4" />
                    <h1 className="text-lg font-bold text-slate-900 mb-2">Lien invalide</h1>
                    <p className="text-sm text-slate-600">{error}</p>
                </div>
            </div>
        );
    }
    if (step === 5 && submittedResult) {
        return (
            <div className="min-h-screen flex items-center justify-center p-4 bg-slate-50">
                <div className="bg-white rounded-2xl border border-slate-200 p-8 max-w-lg text-center"
                     data-testid="success-screen">
                    <Check className="w-12 h-12 text-emerald-500 mx-auto mb-4" />
                    <h1 className="text-xl font-bold text-slate-900 mb-2">Merci pour votre avis</h1>
                    <p className="text-sm text-slate-600 mb-2">
                        Votre avis a été reçu et est en cours de vérification.
                    </p>
                    <p className="text-xs text-slate-500">
                        Identité publique : <span className="font-medium">{submittedResult.public_display_name}</span>
                    </p>
                    <p className="text-xs text-slate-500 mt-2">
                        Statut : {submittedResult.publication_status === 'published'
                            ? 'Publié immédiatement'
                            : 'En cours de modération'}
                    </p>
                </div>
            </div>
        );
    }

    const allDimensionsSet = ctx?.dimensions.every(d => dimensions[d.key]);

    const handleSubmit = async () => {
        if (!consent) {
            toast.error('Le consentement est requis pour publier votre avis.');
            return;
        }
        setSubmitting(true);
        try {
            const r = await axios.post(`${API}/public/seller-reviews/by-token/${token}/submit`, {
                dimensions,
                free_comment: freeComment || null,
                improvement_comment: improvement || null,
                would_recommend: wouldRecommend,
                public_identity_mode: identityMode,
                public_display_name: identityMode === 'full_identity' ? customName : null,
                project_context_public: {
                    property_type: projectType,
                    transaction_type: transactionType,
                },
                consent_confirmed: true,
            });
            setSubmittedResult(r.data);
            setStep(5);
        } catch (err) {
            toast.error(err?.response?.data?.detail || 'Erreur lors de l\'envoi');
        } finally {
            setSubmitting(false);
        }
    };

    const pro = ctx?.professional;

    return (
        <div data-reseau="light" className="reseau-light min-h-screen" style={{ background: 'var(--reseau-bg)' }}>
            <header className="border-b" style={{ background: 'var(--reseau-surface)', borderColor: 'var(--reseau-border)' }}>
                <div className="max-w-2xl mx-auto px-4 py-4">
                    <div className="flex items-center gap-3">
                        <div className="w-11 h-11 rounded-xl flex items-center justify-center"
                             style={{ background: 'var(--reseau-success-soft)' }}>
                            <ShieldCheck className="w-5 h-5" style={{ color: 'var(--reseau-success)' }} />
                        </div>
                        <div className="flex-1 min-w-0">
                            <p className="reseau-eyebrow">Avis vérifié Homly · ~3 minutes</p>
                            <h1 className="reseau-h2 text-base truncate">
                                Votre expérience avec {pro?.first_name} {pro?.last_name_initial}.
                            </h1>
                            <p className="text-xs truncate" style={{ color: 'var(--reseau-ink-muted)' }}>
                                {ctx?.agency?.name} · Vos coordonnées restent privées
                            </p>
                        </div>
                        <span className="text-xs font-extrabold reseau-gradient-text hidden sm:inline">Homly</span>
                    </div>
                </div>
            </header>

            <main className="max-w-2xl mx-auto px-4 py-6 space-y-4 pb-40 sm:pb-48">

                {/* Étape 1 : dimensions */}
                {step === 1 && (
                    <div className="space-y-3">
                        <h2 className="text-lg font-bold text-slate-900">1. Évaluation de l&apos;accompagnement</h2>
                        <p className="text-sm text-slate-600">Notez chaque dimension de 1 (Insuffisant) à 5 (Excellent).</p>
                        {ctx.dimensions.map(d => (
                            <DimensionRow key={d.key} dimKey={d.key} dim={d}
                                          value={dimensions[d.key]}
                                          onChange={(k, v) => setDimensions(prev => ({ ...prev, [k]: v }))} />
                        ))}
                    </div>
                )}

                {/* Étape 2 : questions complémentaires */}
                {step === 2 && (
                    <div className="space-y-4">
                        <h2 className="text-lg font-bold text-slate-900">2. Quelques précisions</h2>
                        <div className="bg-white rounded-xl border border-slate-200 p-4">
                            <label className="block text-sm font-medium text-slate-900 mb-2">
                                Point principal apprécié <span className="text-slate-400 font-normal">(facultatif)</span>
                            </label>
                            <textarea
                                value={freeComment}
                                onChange={e => setFreeComment(e.target.value)}
                                maxLength={2000}
                                rows={3}
                                placeholder="Qu'avez-vous particulièrement apprécié ?"
                                className="w-full p-2 border border-slate-200 rounded-lg text-sm"
                                data-testid="input-free-comment"
                            />
                        </div>
                        <div className="bg-white rounded-xl border border-slate-200 p-4">
                            <label className="block text-sm font-medium text-slate-900 mb-2">
                                Point pouvant être amélioré <span className="text-slate-400 font-normal">(facultatif)</span>
                            </label>
                            <textarea
                                value={improvement}
                                onChange={e => setImprovement(e.target.value)}
                                maxLength={1000}
                                rows={2}
                                placeholder="Ce commentaire n'est pas publié si vous laissez vide."
                                className="w-full p-2 border border-slate-200 rounded-lg text-sm"
                                data-testid="input-improvement"
                            />
                        </div>
                        <div className="bg-white rounded-xl border border-slate-200 p-4">
                            <label className="block text-sm font-medium text-slate-900 mb-2">
                                Recommanderiez-vous ce professionnel ?
                            </label>
                            <div className="flex gap-2">
                                {[
                                    { v: true, label: 'Oui' },
                                    { v: false, label: 'Non' },
                                    { v: null, label: 'Ne se prononce pas' },
                                ].map(o => (
                                    <button key={String(o.v)} type="button"
                                            onClick={() => setWouldRecommend(o.v)}
                                            data-testid={`recommend-${o.v}`}
                                            className={`px-3 py-2 rounded-lg text-sm border transition ${
                                                wouldRecommend === o.v
                                                    ? 'bg-[#681793] text-white border-[#681793]'
                                                    : 'bg-white text-slate-700 border-slate-200'
                                            }`}>
                                        {o.label}
                                    </button>
                                ))}
                            </div>
                        </div>
                        <div className="bg-white rounded-xl border border-slate-200 p-4 space-y-3">
                            <p className="text-sm font-medium text-slate-900">Contexte général</p>
                            <div className="grid grid-cols-2 gap-3">
                                <label className="text-xs text-slate-600">
                                    Type de bien
                                    <select value={projectType} onChange={e => setProjectType(e.target.value)}
                                            className="mt-1 w-full p-2 border border-slate-200 rounded-lg text-sm"
                                            data-testid="select-project-type">
                                        <option value="appartement">Appartement</option>
                                        <option value="maison">Maison</option>
                                        <option value="autre">Autre</option>
                                    </select>
                                </label>
                                <label className="text-xs text-slate-600">
                                    Type de transaction
                                    <select value={transactionType} onChange={e => setTransactionType(e.target.value)}
                                            className="mt-1 w-full p-2 border border-slate-200 rounded-lg text-sm"
                                            data-testid="select-transaction-type">
                                        <option value="vente_principale">Vente principale</option>
                                        <option value="vente_locative">Vente d&apos;un bien locatif</option>
                                        <option value="autre">Autre</option>
                                    </select>
                                </label>
                            </div>
                            <p className="text-xs text-slate-400">
                                Ces informations sont délibérément générales pour rester non identifiables.
                            </p>
                        </div>
                    </div>
                )}

                {/* Étape 3 : identité publique */}
                {step === 3 && (
                    <div className="space-y-4">
                        <h2 className="text-lg font-bold text-slate-900">3. Votre identité publique</h2>
                        <p className="text-sm text-slate-600">
                            Choisissez la manière dont vous apparaîtrez sur le profil de votre agent. Vous restez anonyme par défaut.
                        </p>
                        {ctx.identity_modes.map(m => (
                            <label key={m.key}
                                   className={`block bg-white rounded-xl border p-4 cursor-pointer ${
                                       identityMode === m.key ? 'border-[#681793] ring-2 ring-[#681793]/20' : 'border-slate-200'
                                   }`}
                                   data-testid={`identity-${m.key}`}>
                                <input type="radio" name="id-mode" className="mr-2"
                                       checked={identityMode === m.key}
                                       onChange={() => setIdentityMode(m.key)} />
                                <span className="text-sm font-medium text-slate-900">{m.label}</span>
                            </label>
                        ))}
                        {identityMode === 'full_identity' && (
                            <input type="text"
                                   placeholder="Votre nom complet"
                                   value={customName}
                                   onChange={e => setCustomName(e.target.value)}
                                   className="w-full p-2 border border-slate-200 rounded-lg text-sm"
                                   data-testid="input-custom-name" />
                        )}
                    </div>
                )}

                {/* Étape 4 : aperçu + consentement */}
                {step === 4 && (
                    <div className="space-y-4">
                        <h2 className="text-lg font-bold text-slate-900">4. Aperçu et consentement</h2>
                        <div className="bg-white rounded-xl border border-slate-200 p-5"
                             data-testid="preview-card">
                            <p className="text-xs text-slate-500 uppercase tracking-wide mb-2">Ce qui sera publié</p>
                            <p className="text-sm font-semibold text-slate-900">
                                {identityMode === 'anonymous_verified' ? 'Vendeur vérifié'
                                    : identityMode === 'first_name_only' ? 'Votre prénom'
                                    : identityMode === 'full_identity' ? (customName || 'Votre identité complète')
                                    : 'Votre prénom + initiale'}
                            </p>
                            {freeComment && <p className="text-sm text-slate-700 mt-2">« {freeComment} »</p>}
                            <div className="flex flex-wrap gap-1 mt-3">
                                {Object.entries(dimensions).map(([k, v]) => (
                                    <span key={k} className="text-xs text-slate-600 bg-slate-50 px-2 py-1 rounded">
                                        {(ctx.dimensions.find(d => d.key === k) || {}).label} : {v}/5
                                    </span>
                                ))}
                            </div>
                        </div>
                        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                            <p className="text-sm text-amber-900 mb-3">
                                <strong>Avant de publier :</strong> votre avis sera affiché publiquement sur le profil
                                Homly du professionnel. Il sera vérifié par Homly. Le professionnel pourra y répondre publiquement.
                                Aucune donnée privée (adresse, prix, mandat) ne sera publiée.
                            </p>
                            <label className="flex items-start gap-2 cursor-pointer">
                                <input type="checkbox" className="mt-1"
                                       checked={consent} onChange={e => setConsent(e.target.checked)}
                                       data-testid="consent-checkbox" />
                                <span className="text-sm text-slate-800">
                                    Je confirme vouloir publier cet avis sur le profil Homly du professionnel.
                                </span>
                            </label>
                        </div>
                    </div>
                )}
            </main>

            {/* Barre de navigation collante */}
            <div className="fixed bottom-0 left-0 right-0 bg-white border-t p-3 z-40">
                <div className="max-w-2xl mx-auto flex items-center justify-between gap-2">
                    <span className="text-xs text-slate-500">Étape {step}/4</span>
                    <div className="flex gap-2">
                        {step > 1 && step < 5 && (
                            <Button variant="ghost" onClick={() => setStep(s => s - 1)}
                                    data-testid="btn-prev">Précédent</Button>
                        )}
                        {step < 4 && (
                            <Button onClick={() => setStep(s => s + 1)}
                                    disabled={step === 1 && !allDimensionsSet}
                                    className="bg-[#681793] hover:bg-[#7d2aac]"
                                    data-testid="btn-next">Continuer</Button>
                        )}
                        {step === 4 && (
                            <Button onClick={handleSubmit}
                                    disabled={!consent || submitting}
                                    className="bg-[#681793] hover:bg-[#7d2aac]"
                                    data-testid="btn-submit-review">
                                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Publier mon avis'}
                            </Button>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}
